import { Injectable, NgZone } from '@angular/core';
import { PluginListenerHandle, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { DeviceVolume } from '../plugins/device-volume.plugin';
import { App as CapacitorApp, BackButtonListenerEvent, URLOpenListenerEvent } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { TextZoom } from '@capacitor/text-zoom';
import { environment } from 'src/environments/environment';
import config from 'capacitor.config';
import { RequestService } from './request.service';
import { AppInfo } from '../models/app-info.model';
import { Browser } from '@capacitor/browser';
import { Router } from '@angular/router';
import { AppPlatforms } from '../enums/app-platforms.enum';
import { isNativeApp, getAppPlatform } from 'src/app/util/app-platform';

@Injectable({
  providedIn: 'root'
})
export class CapacitorService {
  redirectUri = `${config.appId}://${window.location.hostname}/capacitor/callback`;
  currentPlatform = getAppPlatform();

  appUrlOpenListener!: PluginListenerHandle;

  constructor(
    private zone: NgZone,
    private router: Router,
    private requestService: RequestService
  ) {}

  getAppInfo(): Promise<AppInfo> {
    return this.requestService.get(`/appInfo/${this.currentPlatform}`);
  }

  getRedirectUri(): string {
    return this.redirectUri;
  }

  initializeApp(): void {
    TextZoom.set({ value: 1.0 });
    this.exitAppListenerCapacitor();
    SplashScreen.hide();

    if (this.currentPlatform === AppPlatforms.ANDROID) {
      void this.configureAndroidSystemBars();
    }
  }

  private async configureAndroidSystemBars(): Promise<void> {
    // The existing patient UI expects the WebView to begin below the status
    // bar. In particular, authentication screens position their logo relative
    // to that inset viewport rather than to the physical top of the display.
    await SystemBars.setStyle({ style: SystemBarsStyle.Dark });
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setBackgroundColor({ color: '#29293B' });
    await StatusBar.setStyle({ style: Style.Dark });
  }

  private exitAppListenerCapacitor(): void {
    CapacitorApp.addListener('backButton', ({ canGoBack }: BackButtonListenerEvent) => {
      if (canGoBack) window.history.back();
      else CapacitorApp.exitApp();
    });
  }

  // used to handle redirect url when user is redirected from a web/link to the app
  async addAppUrlOpenListener(action?: () => void, replaceUrl = false): Promise<void> {
    if (!this.isNativePlatform()) return;

    this.appUrlOpenListener = await CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const path = event.url.split('llamandoaldoctor.com').pop();
        if (path) {
          if (action) action();
          this.router.navigateByUrl(path, { replaceUrl });
          this.closeBrowser();
        }
      });
    });
  }

  async removeAppUrlOpenListener(): Promise<void> {
    await this.appUrlOpenListener?.remove();
  }

  redirectToAppStore(): void {
    const { ANDROID_APP_STORE, IOS_APP_STORE } = environment;

    if (this.currentPlatform === AppPlatforms.ANDROID) window.location.href = ANDROID_APP_STORE;
    else if (this.currentPlatform === AppPlatforms.IOS) window.location.href = IOS_APP_STORE;
  }

  isNativePlatform(): boolean {
    return isNativeApp();
  }

  closeBrowser(): void {
    if (getAppPlatform() === 'ios') Browser.close();
  }

  openBrowser(url: string, windowName = '_self'): void {
    Browser.open({ url, windowName });
  }

  async getDeviceVolume(): Promise<number | undefined> {
    if (!this.isNativePlatform()) return;
    const { level } = await DeviceVolume.getVolume();
    return level;
  }

  async addVolumeChangeListener(callback: (event: { level: number }) => void): Promise<PluginListenerHandle> {
    return DeviceVolume.addListener('volumeChange', callback);
  }
}
